/* import libraries */
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useLocation, useSearchParams } from 'react-router-dom';

/* import components */
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Menu from '../components/Menu';
import IssueSummary from '../components/IssueSummary';
import ClosedMessage from '../components/ClosedMessage';
import CameraIcon from '../components/icons/CameraIcon';
import SendIcon from '../components/icons/SendIcon';
import SpinnerIcon from '../components/icons/SpinnerIcon';
import Loading from './Loading';
import Error from './Error';

/* import services */
import ticketsApi from '../api/ticketsApi';
import { getDateTime, saveToken } from '../utils/utils';
import { supportedFileTypes } from '../utils/constants';
import { EXCLUDED_IMG } from '../components/variables/variables';

const IssueDetails = () => {
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [issue, setIssue] = useState(state?.issue);
    const [commentsBody, setCommentsBody] = useState({"comment" : ""})
    const [submitted, setSubmitted] = useState(false);
    const [typeError, setTypeError] = useState(false);
    const { ticketId } = useParams();
    const bottomOfChatRef = useRef(null);
    const imageRef = useRef();

    const getTicketById = async (id) => {
        try {
            setLoading(true);
            const response = await ticketsApi.getTicketById(id);
            if (response.status) {
                setIssue(response.data.data); 
            } else {
                console.error(response.error.msg);
            }
        } catch (error) {
            setLoadError(true);
            console.error("Error while fetching ticket: " + error.message)
        } finally {
            setLoading(false);
            bottomOfChatRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const addComment = async (body, id) => {
        try {
            setLoading(true);
            const response = await ticketsApi.createComment({body: body, ticketId: id})
            if (response.status) {
                setSubmitted(true);
                setCommentsBody({"comment" : ""});
            } else {
                console.error(response.error.msg);
            }
        } catch (error) {
            setLoadError(true);
            console.error("Error while posting comment: " + error.message)
        } finally {
            setLoading(false);
            bottomOfChatRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleFieldChange = (fieldValue) => {
        setCommentsBody({"comment":fieldValue});
    }; 

    const handleSubmit = (e) => {
        e.preventDefault();
        if (commentsBody.comment.length > 0) {
            addComment(commentsBody, ticketId);
            setSubmitted(false);
            bottomOfChatRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleUpload = () => {
        imageRef.current?.click();
    }

    const handleSelectImageUpload = () => {
        for (let i=0; i<imageRef.current.files.length; i++) {
            let fileType = imageRef.current.files[i].type;
            if (!supportedFileTypes.hasOwnProperty(fileType)) {
                setTypeError(true);
                return;
            }
        }
        setTypeError(false);
        if (imageRef.current.files.length === 0) {
            return;
        }
        const formData = new FormData();
        formData.append('comment', 'Attachment');
        for (let i = 0; i < imageRef.current?.files.length; i++) {
            formData.append(`attachments${i}`, imageRef.current.files[i]);
        }
        addComment(Object.fromEntries(formData), ticketId);
        imageRef.current.value = '';
        setSubmitted(false);
        bottomOfChatRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    const getHtmlBody = (body) => {
        const excludedImg = EXCLUDED_IMG;
        const divElement = document.createElement('div');
        divElement.innerHTML = body;
        const imgElements = divElement.querySelectorAll('img');
        imgElements.forEach(imgElement => {
            if (imgElement.src.includes(excludedImg)) {
                imgElement.remove();
            }
        });
        return divElement.innerHTML;
    }

    const displayAttachment = (attachment) => {
        switch (attachment['content_type']) {
            case "image/png": case "image/jpeg":
                return (<div><img src={attachment['content_url']} width="100" /><br/></div>);
            case "application/pdf": case "application/msword":
                return (<div><a href={attachment['content_url']}>{attachment['file_name']}</a><br/></div>);
            case "video/mp4":
                return (
                    <div>
                        <video width="100" controls >
                            <source src={attachment['content_url']} type="video/mp4"/>
                        </video>
                        <br/>
                    </div>
                )
        }
    }

    useEffect(() => {
        // Save the token if opened from a mobile entry point
        if (searchParams.get("token")) {
            saveToken(searchParams.get("token"), setLoading);
        }

        getTicketById(ticketId);
    }, [submitted])

    useEffect(() => {
        bottomOfChatRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [issue]);

    if (!issue) return <Loading />
    if (loadError) return <Error />

    return (
        <>
            <Menu title="Support Requests" />
            <Container style={{ paddingTop: "20px"}}>
                <div className="alignTextDiffSize">
                    <span className="grey font-bold" style={{ fontSize: "21px"}}>
                        {issue?.subject}
                    </span>
                </div>
                <p className="grey topics">{getDateTime(issue?.createdAt, "unix")}</p>

                {issue ? 
                <>
                    <IssueSummary issue={issue} />
                    {issue.comments?.map((comment,i) => 
                    <>
                        <Card key={i} className={comment.author === 'member' ? "toMessage" : "fromMessage"}>
                            <Card.Body>
                                <div style={{ fontSize: "14px" }} dangerouslySetInnerHTML={{__html: getHtmlBody(comment.htmlBody)}} />
                                {comment.attachments.length > 0 ? 
                                    comment.attachments.map((attachment,i) => <div key={i}>{displayAttachment(attachment)}</div>) : <></>} 
                            </Card.Body>
                        </Card>
                        <p className={comment.author === 'member' ? 'sendTime': 'receiveTime'}>
                            {getDateTime(comment.createdAt, "unix")}
                        </p>
                    </>)}
                </> 
                :<></>}
                <div ref={bottomOfChatRef} />
                {issue && issue.status !== 'closed' ? 
                <p className="issueMsg">Thank you for contacting our support team, please note that our agents will respond to 
                your inquiry within 24 hours. Appreciate your patience.
                </p> : <div className="issueMsg"></div>}
            </Container>
            {issue && issue.status !== 'closed' ? 
            <Card className="messageBox">
                <div className="font-bold" style={{ fontSize: "14px", color: "red"}}>
                    {typeError ? "Invalid file type." : ""}
                </div>
                <Form onSubmit={handleSubmit} style={{ display: 'flex' }}> 
                    <CameraIcon onClick={handleUpload}/>
                    <input
                        type="file" 
                        ref={imageRef} 
                        className="d-none"
                        multiple
                        onChange={handleSelectImageUpload}/>
                    <Form.Control
                        id="chat"
                        className="sendMessage"
                        size="lg"
                        type="text" 
                        placeholder="Message"
                        disabled={issue.status === 'closed' ? true : false}
                        value={commentsBody['comment']}
                        onChange={(e) => handleFieldChange(e.target.value)}
                    />
                    {loading ? <SpinnerIcon /> : <SendIcon onClick={handleSubmit}/>}
                </Form>   
                <div style={{ marginBottom: '20px'}} />
            </Card> : 
            issue && issue.status === 'closed' ? 
            <ClosedMessage /> : <></>}
        </>
    );
}

export default IssueDetails;